import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { AlertTriangle, ArrowRight, Megaphone, Play, Sparkles, Trash2, X } from "lucide-react";
import type { GalleryTemplate } from "@/lib/campaigns";
import { apiErrorMessage } from "@/lib/api";
import { useBrands } from "@/lib/queries";
import {
  useCampaigns,
  useCancelCampaign,
  useCreateCampaign,
  useGenerateCampaign,
  usePreviewCampaign,
  useTemplates,
  type CampaignPreview,
} from "@/lib/campaigns";
import { BatchProgress } from "@/components/BatchProgress";
import { PagePicker } from "@/components/PagePicker";
import { ErrorBox } from "./LoginPage";

/**
 * Campaign gallery and runner (spec §6 flow 4, §8).
 *
 * The flow is pick → set dates and details → PREVIEW → create → generate.
 * The preview step is not decoration: spec §9 requires showing how many posts
 * a campaign will consume before it runs, and generation is the only expensive
 * part. Creating is cheap and reversible; generating is neither.
 */
export function CampaignsPage() {
  const { t } = useTranslation();
  const brands = useBrands();
  const [brandId, setBrandId] = useState<string | null>(null);

  useEffect(() => {
    if (!brandId && brands.data?.length) setBrandId(brands.data[0]!.id);
  }, [brands.data, brandId]);

  const templates = useTemplates(brandId);
  const campaigns = useCampaigns(brandId);
  const preview = usePreviewCampaign();
  const create = useCreateCampaign(brandId);
  const generate = useGenerateCampaign(brandId);
  const cancel = useCancelCampaign(brandId);

  const [chosen, setChosen] = useState<GalleryTemplate | null>(null);
  /**
   * Whether the composer is open, held separately from which template is in it.
   *
   * It has to be separate, because a CUSTOM campaign has no template: `chosen`
   * is legitimately null while the composer is open. Inferring "open" from
   * `chosen !== null` therefore made "Start a custom campaign" a no-op — it
   * set chosen to null, which is what closed means, so the panel it was
   * supposed to open never appeared. Opening and closing were literally the
   * same call.
   */
  const [composing, setComposing] = useState(false);
  const [name, setName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [postCount, setPostCount] = useState(5);
  const [notes, setNotes] = useState("");
  const [plan, setPlan] = useState<CampaignPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Empty = every connected page for this brand. */
  const [pages, setPages] = useState<string[]>([]);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * Why posts failed, when some did.
   *
   * The service has always returned these and the screen has always thrown
   * them away, so "2 failed" was the entire account of what went wrong — and
   * the actual reason, a 400 from the model API naming the exact field it
   * refused, was only ever in a server log nobody was watching.
   */
  const [failures, setFailures] = useState<string[]>([]);

  /** Opens the composer, with a template or empty for a custom campaign. */
  function openTemplate(template: GalleryTemplate | null) {
    setComposing(true);
    setChosen(template);
    setPlan(null);
    setError(null);
    setNotice(null);
    setName(template?.name ?? "");
    setPostCount(template?.postCountDefault ?? 5);
    setNotes("");

    // Holidays with a fixed date pre-fill the window (spec §6); the client can
    // still change it.
    if (template?.suggestedWindow) {
      setStartDate(template.suggestedWindow.startDate);
      setEndDate(template.suggestedWindow.endDate);
    } else {
      const today = new Date();
      const in14 = new Date(today.getTime() + 14 * 86_400_000);
      setStartDate(today.toISOString().slice(0, 10));
      setEndDate(in14.toISOString().slice(0, 10));
    }
  }

  /** Closes it and clears what was being composed. */
  function closeComposer() {
    setComposing(false);
    setChosen(null);
    setPlan(null);
    setError(null);
  }

  const grouped = useMemo(() => {
    const out = new Map<string, GalleryTemplate[]>();
    for (const template of templates.data ?? []) {
      const list = out.get(template.category) ?? [];
      list.push(template);
      out.set(template.category, list);
    }
    return [...out.entries()];
  }, [templates.data]);

  async function onPreview() {
    if (!brandId) return;
    setError(null);
    try {
      setPlan(
        await preview.mutateAsync({
          brandId,
          templateKey: chosen?.key ?? null,
          startDate,
          endDate,
          postCount,
          socialAccountIds: pages,
        }),
      );
    } catch (err) {
      setPlan(null);
      setError(apiErrorMessage(err, t("campaigns.previewFailed")));
    }
  }

  async function onCreate() {
    if (!brandId) return;
    setError(null);
    try {
      const result = await create.mutateAsync({
        brandId,
        templateKey: chosen?.key ?? null,
        name,
        startDate,
        endDate,
        postCount,
        socialAccountIds: pages,
        notes: notes || undefined,
      });
      setNotice(t("campaigns.created", { count: result.postCount }));
      closeComposer();
    } catch (err) {
      setError(apiErrorMessage(err, t("campaigns.createFailed")));
    }
  }

  async function onGenerate(campaignId: string) {
    setError(null);
    setNotice(null);
    setFailures([]);
    try {
      const result = await generate.mutateAsync(campaignId);
      setNotice(
        result.failed > 0
          ? t("campaigns.generatedPartial", {
              generated: result.generated,
              failed: result.failed,
            })
          : t("campaigns.generated", {
              count: result.generated,
              sent: result.approvalSent?.sent ?? 0,
            }),
      );
      // De-duplicated: when every post fails it is almost always the same
      // cause repeated, and ten copies of one message reads as ten problems.
      setFailures([...new Set(result.errors ?? [])]);
    } catch (err) {
      setError(apiErrorMessage(err, t("campaigns.generateFailed")));
    }
  }

  if (brands.isLoading) return <p className="text-sm text-muted">{t("errors.loading")}</p>;
  if (!brands.data?.length) return <p className="text-sm text-muted">{t("campaigns.noBrands")}</p>;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2.5 text-xl font-semibold tracking-tight text-ink">
            <Megaphone size={18} className="text-primary-600" />
            {t("campaigns.title")}
          </h1>
          <p className="text-sm text-muted">{t("campaigns.subtitle")}</p>
        </div>
        <select
          value={brandId ?? ""}
          onChange={(e) => {
            setBrandId(e.target.value);
            // A half-filled campaign belongs to the brand it was started for.
            closeComposer();
          }}
          className="select"
        >
          {brands.data.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </header>

      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {notice ? (
        <div className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-sm text-green-800">
          {notice}
        </div>
      ) : null}
      {failures.length ? (
        <div className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm">
          <p className="font-medium text-warning">{t("campaigns.whyFailed")}</p>
          <ul className="mt-1 space-y-1 text-muted">
            {failures.map((reason) => (
              // Verbatim. These are the provider's own words, and paraphrasing
              // them into something friendlier is how the one detail that
              // identifies the fault gets lost.
              <li key={reason} className="break-words font-mono text-xs">
                {reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {/* Running campaigns first — they need action, the gallery does not. */}
      {campaigns.data?.length ? (
        <section className="card-pad">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-sm font-semibold">{t("campaigns.yours")}</h2>
            {/* A generated campaign's posts land on the calendar, and there
                was nothing on this screen saying so — the campaign appeared
                here, the posts appeared nowhere the owner was looking. */}
            <Link
              to="/calendar"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline"
            >
              {t("campaigns.seePosts")}
              <ArrowRight size={14} className="flip-rtl" />
            </Link>
          </div>
          <ul className="space-y-2">
            {campaigns.data.map((campaign) => (
              <li
                key={campaign.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2"
              >
                <div className="min-w-0">
                  <span className="font-medium">{campaign.name}</span>
                  <span className="ml-2 text-xs text-muted">
                    {campaign.startDate.slice(0, 10)} → {campaign.endDate.slice(0, 10)} ·{" "}
                    {t("campaigns.postCount", { count: campaign._count.posts })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <BatchProgress posts={campaign.posts ?? []} storedStatus={campaign.status} />
                  {campaign.status === "draft" ? (
                    <button
                      onClick={() => void onGenerate(campaign.id)}
                      disabled={generate.isPending}
                      className="btn-primary btn-sm"
                    >
                      <Play size={13} />
                      {generate.isPending ? t("campaigns.generating") : t("campaigns.generate")}
                    </button>
                  ) : null}
                  {campaign.status !== "cancelled" && campaign.status !== "completed" ? (
                    <button
                      onClick={() => void cancel.mutateAsync(campaign.id)}
                      className="text-muted hover:text-red-600"
                      aria-label={t("campaigns.cancel")}
                    >
                      <Trash2 size={14} />
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* The composer, open for a template or for a custom campaign. */}
      {composing ? (
        <section className="space-y-3 rounded-xl border border-primary-300 bg-primary-50/40 p-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold">
              {chosen ? chosen.name : t("campaigns.customCampaign")}
            </h2>
            <button
              onClick={closeComposer}
              aria-label={t("campaigns.closeComposer")}
              className="text-muted hover:text-ink"
            >
              <X size={16} />
            </button>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("campaigns.nameLabel")}</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{t("campaigns.startDate")}</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPlan(null);
                }}
                className="input"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{t("campaigns.endDate")}</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPlan(null);
                }}
                className="input"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium">{t("campaigns.postCountLabel")}</span>
              <input
                type="number"
                min={1}
                max={60}
                value={postCount}
                onChange={(e) => {
                  setPostCount(Number(e.target.value));
                  setPlan(null);
                }}
                className="input"
              />
            </label>
          </div>

          <PagePicker
            brandId={brandId}
            value={pages}
            onChange={(ids) => {
              setPages(ids);
              setPlan(null);
            }}
          />

          <label className="block text-sm">
            <span className="mb-1 block font-medium">{t("campaigns.notesLabel")}</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder={t("campaigns.notesPlaceholder")}
              className="input"
            />
            <span className="mt-1 block text-xs text-muted">{t("campaigns.notesHint")}</span>
          </label>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => void onPreview()}
              disabled={preview.isPending}
              className="rounded-lg border border-border bg-white px-3 py-2 text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              {t("campaigns.preview")}
            </button>
            <button
              onClick={() => void onCreate()}
              disabled={create.isPending || !plan || plan.posts.length === 0}
              className="btn-primary btn-sm"
              title={!plan ? t("campaigns.previewFirst") : undefined}
            >
              {t("campaigns.create")}
            </button>
          </div>

          {plan ? <PlanSummary plan={plan} /> : null}
        </section>
      ) : null}

      {/* The gallery */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">{t("campaigns.galleryTitle")}</h2>
          <button
            onClick={() => openTemplate(null)}
            className="inline-flex items-center gap-1 text-sm text-primary-700 hover:underline"
          >
            <Sparkles size={14} /> {t("campaigns.startCustom")}
          </button>
        </div>

        {templates.isLoading ? <p className="text-sm text-muted">{t("errors.loading")}</p> : null}

        {grouped.map(([category, list]) => (
          <div key={category}>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
              {t(`campaigns.categories.${category}`)}
            </h3>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((template) => (
                <button
                  key={template.key}
                  onClick={() => openTemplate(template)}
                  className="rounded-lg border border-border bg-white p-3 text-left text-sm hover:border-primary-400 hover:bg-primary-50"
                >
                  <span className="flex items-center gap-2 font-medium">
                    {template.name}
                    {template.local ? (
                      <span className="rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-800">
                        {t("campaigns.localBadge")}
                      </span>
                    ) : null}
                  </span>
                  <span className="mt-1 block text-xs text-muted">{template.description}</span>
                  <span className="mt-2 block text-[11px] text-muted">
                    {template.roles
                      .slice(0, 5)
                      .map((r) => t(`campaigns.roles.${r}`))
                      .join(" → ")}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

/** What the campaign will actually produce, before anything is spent. */
function PlanSummary({ plan }: { plan: CampaignPreview }) {
  const { t } = useTranslation();

  return (
    <div className="rounded-lg border border-border bg-white p-3">
      <p className="text-sm font-medium">
        {t("campaigns.willUse", { count: plan.postsConsumed, pages: plan.pageCount })}
      </p>

      {plan.shortfall ? (
        <p className="mt-2 flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-xs text-amber-900">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          {plan.shortfall.reason}
        </p>
      ) : null}

      <ul className="mt-2 space-y-1">
        {plan.posts.map((post) => (
          <li
            key={post.index}
            className="flex justify-between gap-2 rounded border border-border px-2 py-1 text-xs"
          >
            <span className="font-medium">{t(`campaigns.roles.${post.role}`)}</span>
            <span className="font-mono text-muted">{post.day}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone =
    status === "pending_approval"
      ? "bg-amber-100 text-amber-800"
      : status === "active"
        ? "bg-green-100 text-green-800"
        : status === "cancelled"
          ? "bg-gray-100 text-gray-600"
          : status === "generating"
            ? "bg-blue-100 text-blue-800"
            : "bg-gray-100 text-gray-700";

  return (
    <span className={`rounded px-2 py-0.5 text-[11px] font-medium ${tone}`}>
      {t(`campaigns.status.${status}`, status.replace(/_/g, " "))}
    </span>
  );
}
